import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck, Copy, ExternalLink, MapPin, Sparkles, Wifi } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CompanyLogo, ScoreRing, Spinner } from "@/components/Common";
import { Rich } from "@/components/Rich";
import { StatusBadge } from "@/components/JobCard";
import { AtsPassButton } from "@/components/AtsPassButton";
import { TailorResumeButton } from "@/components/TailorResumeButton";
import { usePickBeforeApply } from "@/components/AutoPickPrompt";
import { noteApply } from "@/lib/streak";
import { PreApplyCheck, needsPreCheck } from "@/components/PreApplyCheck";
import { FindPeopleButton } from "@/components/people/FindPeopleButton";
import { JobContacts } from "@/components/people/JobContacts";
import { api, enc, errMsg, streamPost, textOf } from "@/lib/api";

const MatchPanel = ({ jobId }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post(`/ai/match/${enc(jobId)}`)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!res) return (
    <div className="rounded-xl border border-[#27272A] bg-[#141417] p-6 text-center">
      <Sparkles className="mx-auto mb-3 h-6 w-6 text-[#D4AF37]" />
      <p className="mb-4 text-sm text-zinc-400">Score this role against your resume.</p>
      <Button onClick={run} disabled={busy} data-testid="ai-match-button">{busy ? <Spinner /> : "Run AI match"}</Button>
    </div>
  );
  const score = res.score ?? res.match_score ?? res.match?.score;
  const { score: _s, match_score: _m, ...rest } = res;
  return (
    <div className="space-y-5" data-testid="ai-match-result">
      {score != null && <div className="flex items-center gap-4"><ScoreRing score={score} size={80} /><div className="font-display text-lg text-zinc-200">Match score</div></div>}
      <Rich value={rest} />
    </div>
  );
};

const CoverPanel = ({ jobId }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/ai/cover-letter/${enc(jobId)}`).then((r) => setText(textOf(r.data))).catch(() => {}); }, [jobId]);
  const gen = async () => {
    setBusy(true);
    setText("");
    try { await streamPost(`/ai/cover-letter/${enc(jobId)}`, setText); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      {text && <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={16} className="bg-[#09090B] text-sm leading-relaxed" data-testid="cover-letter-text" />}
      <div className="flex gap-2">
        <Button onClick={gen} disabled={busy} data-testid="generate-cover-letter-button">{busy ? <Spinner /> : <Sparkles className="h-4 w-4" />}{text ? "Regenerate" : "Generate cover letter"}</Button>
        {text && <Button variant="outline" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} data-testid="copy-cover-letter-button"><Copy className="h-4 w-4" />Copy</Button>}
      </div>
    </div>
  );
};

export const JobDetailSheet = ({ jobId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const load = () => api.get(`/jobs/${enc(jobId)}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (jobId) { setData(null); load(); } }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const job = data?.job;
  const app = data?.application;
  const changed = async () => { await load(); onChanged?.(); };

  const toggleSave = async () => {
    try {
      if (app) await api.delete(`/applications/${enc(jobId)}`);
      else await api.post("/applications", { job_id: jobId });
      toast.success(app ? "Removed from tracker" : "Saved");
      changed();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const markApplied = async () => {
    try {
      if (!app) await api.post("/applications", { job_id: jobId });
      await api.patch(`/applications/${enc(jobId)}`, { status: "applied" });
      toast.success("Marked as applied");
      changed();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [preCheck, setPreCheck] = useState(false);
  const openApply = () => {
    noteApply();
    window.open(job.apply_link, "_blank", "noopener");
    if (app?.status !== "applied") toast("Did you apply?", { action: { label: "Mark applied", onClick: markApplied } });
  };
  const { start: pickApply, dialog: pickDialog } = usePickBeforeApply(openApply);
  const applyNow = () => pickApply(jobId);
  const apply = () => (needsPreCheck(jobId) ? setPreCheck(true) : applyNow());

  return (
    <Sheet open={!!jobId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-[#27272A] bg-[#09090B] p-0 sm:max-w-2xl" data-testid="job-detail-sheet">
        <SheetTitle className="sr-only">Job detail</SheetTitle>
        {!job ? <div className="flex h-64 items-center justify-center"><Spinner className="h-6 w-6 text-[#D4AF37]" /></div> : (
          <div className="p-6 md:p-8">
            <div className="flex gap-4">
              <CompanyLogo src={job.logo} name={job.company} size="h-14 w-14" />
              <div className="min-w-0">
                <h2 className="font-display text-2xl font-semibold text-zinc-100" data-testid="job-detail-title">{job.title}</h2>
                <p className="text-zinc-400">{job.company}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                  <StatusBadge status={app?.status} />
                  {job.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
                  {job.is_remote && <span className="flex items-center gap-1 text-emerald-400"><Wifi className="h-3 w-3" />Remote</span>}
                  {job.employment_type && <span>{job.employment_type}</span>}
                  {job.salary && <span className="font-mono text-[#F3C649]">{job.salary}</span>}
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {job.apply_link && <Button onClick={apply} className="rounded-full" data-testid="apply-button"><ExternalLink className="h-4 w-4" />Apply</Button>}
              <AtsPassButton job={{ ...job, id: job.id || jobId }} />
              <TailorResumeButton job={{ ...job, id: job.id || jobId }} />
              {preCheck && <PreApplyCheck job={{ ...job, id: jobId }} open={preCheck} onClose={() => setPreCheck(false)} onApply={applyNow} />}
              {pickDialog}
              <Button variant="outline" onClick={toggleSave} className="rounded-full" data-testid="detail-save-button">
                {app ? <BookmarkCheck className="h-4 w-4 text-[#D4AF37]" /> : <Bookmark className="h-4 w-4" />}{app ? "Saved" : "Save"}
              </Button>
              {app?.status !== "applied" && <Button variant="ghost" onClick={markApplied} className="rounded-full" data-testid="mark-applied-button">Mark applied</Button>}
            </div>
            {job.company && <div className="mt-3"><FindPeopleButton job={{ ...job, id: job.id || jobId }} saveFirst saved={!!app} /></div>}
            {app && <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121214] p-4"><JobContacts jobId={jobId} job={job} contacts={app.contacts} /></div>}
            <Tabs defaultValue="about" className="mt-8">
              <TabsList className="bg-[#18181B]">
                <TabsTrigger value="about" data-testid="tab-about">About</TabsTrigger>
                <TabsTrigger value="match" data-testid="tab-match">AI match</TabsTrigger>
                <TabsTrigger value="cover" data-testid="tab-cover">Cover letter</TabsTrigger>
              </TabsList>
              <TabsContent value="about" className="mt-5">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-300" data-testid="job-description">{job.description || job.description_snippet}</p>
                {job.benefits?.length > 0 && <div className="mt-6"><Rich value={{ benefits: job.benefits }} /></div>}
              </TabsContent>
              <TabsContent value="match" className="mt-5"><MatchPanel jobId={jobId} /></TabsContent>
              <TabsContent value="cover" className="mt-5"><CoverPanel jobId={jobId} /></TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
